import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Typography, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { getInvestigation } from "../../../../../apis";
import { startSettingInvestigation } from "../../../../../store/jaguar/jaguarThunks";
import { useTransformedCatalog } from "../../../../../hooks/useTransformedCatalog";
import { getSafeParsedItem, initialCurrentReportLS } from "../../ReportViewer";
import { InvestigationNavigation } from "../components/InvestigationNavigation";
import { InvestigationHeader } from "../components/InvestigationHeader";
import { Folder } from "../Folder/Folder";
import { Evaluation } from "../Evaluation/Evaluation";
import { Scenarios } from "../Scenarios/Scenarios";
import { Involved } from "../Involved/Involved";
import { ActivityPlan } from "../ActivityPlan/ActivityPlan";
import { Resources } from "../Resources/Resources";
import { Interviews } from "../Interviews/Interviews";
import { Evidences } from "../Evidences/Evidences";
import { Conclusion } from "../Conclusion/Conclusion";
import { ModalGeneratePDF } from "../ModalGeneratePDF/ModalGeneratePDF";
import { PrintInvestigation } from "../Modals/PrintInvestigation";
import { AddInvestigation } from "../Modals/AddInvestigation";
import { HireService } from "../Modals/HireService";
import { useConstants } from "./useConstants";
import { Confirmation } from "../../../../Modal/Confirmation/Confirmation";
import { useModalConfirm } from "../../../../../hooks/useModalConfirm";
import NotFound from "../../../../GlobalComponents/NotFound";
import { Loading } from "../../../../Loading/String/Loading";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import "./InvestigationPage.scss";

//---> Segmented data related to the investigation <---//
export const InvestigationPage = ({
  id,
  investigationList = [],
  onReload,
  reportDetail,
  assignedUsers = [],
  companyRelationshipCat,
  grievanceClassification,
  showModalPDFGeneratorInvestigation,
  setShowModalPDFGeneratorInvestigation,
  showModalPrintInvestigation,
  setShowModalPrintInvestigation,
}) => {
  //---> Custom Hooks <---//
  const [t] = useTranslation("investigation");
  const dispatch = useDispatch();
  const theme = useTheme();
  const catalogCompanyRelationship = useTransformedCatalog(
    companyRelationshipCat?.data
  );
  const grievanceClassificationCat = useTransformedCatalog(
    grievanceClassification?.data
  );
  const {
    enums,
    investigation: { allowOnSimple },
  } = useConstants();
  const { modalConfirm, openModalConfirm, closeModalConfirm } =
    useModalConfirm();

  // Get report information from localStorage (LS)
  const currentReportLS =
    getSafeParsedItem("current-report") ?? initialCurrentReportLS(id);
  // Initial States
  const [loadingInvestigation, setLoadingInvestigation] = useState(false);
  // A investigation from investigation list
  const [selectedInvestigation, setSelectedInvestigation] = useState("");
  // Full investigation data
  const [investigation, setInvestigation] = useState({});
  // General loading Investigation section
  const [isLoading, setIsLoading] = useState(true);
  // Select Investigation Section
  const [selectedSection, setSelectedSection] = useState(
    allowOnSimple.includes(currentReportLS["inv-tab"])
      ? currentReportLS["inv-tab"]
      : "researchFolder"
  );
  // Show modal add new investigation confirm
  const [showModalAddInvestigation, setShowModalAddInvestigation] =
    useState(false);
  // Show modal hire service
  const [showModalHireService, setShowModalHireService] = useState(false);

  //---> Set current investigation <---//
  useEffect(() => {
    const defaultInvestigation = checkInvestigationExist(
      currentReportLS["inv-id"]
    );
    if (defaultInvestigation) {
      updateSelectedInvestigation(defaultInvestigation);
    }
  }, [investigationList]);

  // When an investigation is selected, its characteristics are consulted
  const updateSelectedInvestigation = async (investigation, change) => {
    setSelectedInvestigation(investigation);
    setIsLoading(true);
    const { advance = false } = investigationList.find(
      (item) => item.alias === investigation
    );
    if (!advance && change) {
      hdlSelect("researchFolder", investigation);
    }
    // Makes investigation request
    try {
      const resp = await getInvestigation(investigation);
      setInvestigation({
        ...resp.response,
      });
      dispatch(startSettingInvestigation(resp.response));
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const reloadInvestigation = () => {
    updateSelectedInvestigation(selectedInvestigation);
  };

  //---> Evaluate investigations related to this report <---//
  const checkInvestigationExist = (invAlias = "") => {
    if (!Array.isArray(investigationList) || investigationList.length === 0)
      return "";
    if (investigationList.length === 1) return investigationList[0].alias;
    const found = investigationList.find((item) => item.alias === invAlias);
    return found ? found.alias : investigationList[0].alias;
  };

  // Select another investigation section
  const hdlSelect = (key, otherId) => {
    setSelectedSection(key);
    const currentReportLS =
      getSafeParsedItem("current-report") ??
      initialCurrentReportLS(otherId ?? id);
    localStorage.setItem(
      "current-report",
      JSON.stringify({ ...currentReportLS, "inv-tab": key })
    );
  };

  const openConfirmDeleteModal = ({
    onConfirm,
    title,
    description,
    question,
  }) => {
    openModalConfirm({
      variant: "error",
      ethiquin: "megaphone",
      title: (
        <Typography
          color="pink.main"
          fontSize={28}
          fontWeight={700}
          textAlign="start"
        >
          {title || t("General.Warning")}
        </Typography>
      ),
      titleColor: "pink",
      description1: (
        <Typography color="oxfordBlue.main" fontSize={16} textAlign="start">
          {description ||
            t("General.only_delete_information")}
        </Typography>
      ),
      question: (
        <Typography
          color="primary.main"
          fontSize={16}
          fontWeight={600}
          textAlign="start"
        >
          {question || t("General.continue")}
        </Typography>
      ),
      leftButtonText: t("General.cancel"),
      leftButtonVariant: "contained",
      onClickLeft: closeModalConfirm,
      rightButtonText: t("General.delete"),
      rightButtonColor: "error",
      rightButtonVariant: "contained",
      onClickRight: onConfirm,
    });
  };

  return (
    <>
      {/* Create a new investigation modal */}
      <AddInvestigation
        show={showModalAddInvestigation}
        onHide={() => setShowModalAddInvestigation(false)}
        id={id}
        reportRender={onReload}
      />

      {!investigationList.length ? (
        <Box
          bgcolor={theme.palette.background.paper}
          className="rounded dyBorder1"
          sx={{
            mt: 2,
            p: 2,
            display: "flex",
            alignItems: "center",
            flexFlow: "column nowrap",
            gap: 2,
          }}
        >
          <Typography variant="h4" textAlign="center">
            {t("investigation.report_dont_have_investigation")}
          </Typography>
          <EGButton
            label={t("investigation.create_investigation")}
            color="primary"
            iconName="outline_search_user"
            iconSize={20}
            labelColorHover="white"
            onClick={() => setShowModalAddInvestigation(true)}
          />
          <NotFound />
        </Box>
      ) : isLoading || !investigation?.alias ? (
        <Box
          bgcolor={theme.palette.background.paper}
          className="rounded dyBorder1"
          sx={{
            mt: 2,
            p: 2,
            display: "flex",
            alignItems: "center",
            flexFlow: "column nowrap",
            gap: 2,
          }}
        >
          <Loading />
        </Box>
      ) : (
        <Box>
          {/* Dynamic Modal to confirm actions */}
          <Confirmation {...modalConfirm} onHide={closeModalConfirm} />
          {/* Modal for generating PDFs  */}
          <ModalGeneratePDF
            show={showModalPDFGeneratorInvestigation}
            onHide={() => setShowModalPDFGeneratorInvestigation(false)}
          />
          {/* Confirm print modal */}
          <PrintInvestigation
            show={showModalPrintInvestigation}
            onHide={() => setShowModalPrintInvestigation(false)}
          />
          {/*  */}
          <HireService
            show={showModalHireService}
            onHide={() => setShowModalHireService(false)}
          />
          {/* Investigation Header (name and select investigation) */}

          <InvestigationHeader
            id={reportDetail.id}
            reportDetail={reportDetail}
            investigationList={investigationList}
            investigation={investigation}
            selectedInvestigation={selectedInvestigation}
            updateSelectedInvestigation={updateSelectedInvestigation}
            reportRender={onReload}
            setLoadingInvestigation={setLoadingInvestigation}
            setShowModalAddInvestigation={setShowModalAddInvestigation}
            openConfirmDeleteModal={openConfirmDeleteModal}
          />
          {/* Folders on investigation*/}
          <InvestigationNavigation
            activeFolder={selectedSection}
            advance={investigation.advance}
            closed={!!investigation.conclusion_at}
            hdlSelect={hdlSelect}
            investigation={investigation}
            setShowModalHireService={setShowModalHireService}
          >
            <Folder
              tabProps={{
                key: enums.sections.researchFolder,
                name: t("sections.investigation_folder"),
                icon: "outline_folder_user",
                iconSelected: "bold_folder_user",
                color: !investigation.conclusion_at
                  ? investigation.advance
                    ? theme.palette.ethicsBlue.main
                    : theme.palette.green.main
                  : theme.palette.red.main,
                colorSelected: theme.palette.red.main,
              }}
              catalogCompanyRelationship={catalogCompanyRelationship}
              grievanceClassificationCat={grievanceClassificationCat}
              handleUnlinkInvestigation={() => {}}
              investigation={investigation}
              setInvestigation={setInvestigation}
              reportDetail={reportDetail}
              assignedUsers={assignedUsers}
              loadingInvestigation={loadingInvestigation}
              handleSelectedSection={hdlSelect}
              openModalConfirm={openModalConfirm}
              closeModalConfirm={closeModalConfirm}
            />

            <Evaluation
              tabProps={{
                key: enums.sections.caseEvaluation,
                name: t("sections.evaluation"),
                icon: "outline_task",
                iconSelected: "bold_task",
                color: theme.palette.cobalt.main,
              }}
              investigation={investigation}
              setInvestigation={setInvestigation}
            />
            <Scenarios
              tabProps={{
                key: enums.sections.scenarios,
                name: t("sections.locations"),
                icon: "outline_map_3",
                iconSelected: "bold_map",
                color: theme.palette.green.main,
              }}
              reportDetail={reportDetail}
              investigation={investigation}
              setInvestigation={setInvestigation}
              openConfirmDeleteModal={openConfirmDeleteModal}
              closeModalConfirm={closeModalConfirm}
            />

            <Involved
              tabProps={{
                key: enums.sections.involveds,
                name: t("sections.involved"),
                icon: "outline_profile_2_user",
                iconSelected: "bold_profile_2_ser",
                color: theme.palette.pink.main,
              }}
              investigation={investigation}
              setInvestigation={setInvestigation}
              reportDetail={reportDetail}
            />

            <ActivityPlan
              tabProps={{
                key: enums.sections.activityPlan,
                name: t("sections.activity_plan"),
                icon: "outline_note_2",
                iconSelected: "bold_note_2",
                color: theme.palette.pinkIII.main,
              }}
              investigation={investigation}
              setInvestigation={setInvestigation}
              reloadInvestigation={reloadInvestigation}
            />

            <Resources
              tabProps={{
                key: enums.sections.resources,
                name: t("sections.resources"),
                icon: "outline_data_setting",
                iconSelected: "bold_data_setting",
                color: theme.palette.magentaBliss.main,
              }}
              investigation={investigation}
              setInvestigation={setInvestigation}
              openConfirmDeleteModal={openConfirmDeleteModal}
              closeModalConfirm={closeModalConfirm}
            />

            <Interviews
              tabProps={{
                key: enums.sections.interviews,
                name: t("sections.interviews"),
                icon: "outline_messages_3",
                iconSelected: "bold_messages_3",
                color: theme.palette.teal.main,
              }}
              investigation={investigation}
              setInvestigation={setInvestigation}
              openConfirmDeleteModal={openConfirmDeleteModal}
              closeModalConfirm={closeModalConfirm}
              onReload={reloadInvestigation}
            />

            <Evidences
              tabProps={{
                key: enums.sections.evidences,
                name: t("sections.evidences"),
                icon: "outline_search_evidence",
                iconSelected: "bold_search_evidence",
                color: theme.palette.blueGray800.main,
              }}
              investigation={investigation}
              setInvestigation={setInvestigation}
              openConfirmDeleteModal={openConfirmDeleteModal}
              closeModalConfirm={closeModalConfirm}
              onReload={reloadInvestigation}
            />

            <Conclusion
              tabProps={{
                key: enums.sections.conclusion,
                name: t("sections.conclusion"),
                icon: "outline_clipboard_task",
                iconSelected: "bold_clipboard_task",
                color: theme.palette.orangeII.main,
              }}
              investigation={investigation}
              setInvestigation={setInvestigation}
              reportDetail={reportDetail}
              onReload={reloadInvestigation}
              onReloadReport={onReload}
            />
          </InvestigationNavigation>
        </Box>
      )}
    </>
  );
};
